const workspaceScrollPositions = new Map<string, number>();

type PendingWorkspaceScroll = {
  href: string;
  minHeight: string;
  overflowAnchor: string;
  targetTop: number;
  token: number;
};

let pendingWorkspaceScroll: PendingWorkspaceScroll | null = null;
let pendingCleanupTimer: number | null = null;
let pendingFrame: number | null = null;
let pendingToken = 0;

function releasePendingWorkspaceScroll(token?: number) {
  if (
    !pendingWorkspaceScroll ||
    (token !== undefined && pendingWorkspaceScroll.token !== token)
  ) {
    return;
  }

  const pending = pendingWorkspaceScroll;
  pendingWorkspaceScroll = null;

  if (pendingCleanupTimer !== null) {
    window.clearTimeout(pendingCleanupTimer);
    pendingCleanupTimer = null;
  }
  if (pendingFrame !== null) {
    window.cancelAnimationFrame(pendingFrame);
    pendingFrame = null;
  }

  document.documentElement.style.minHeight = pending.minHeight;
  document.documentElement.style.overflowAnchor = pending.overflowAnchor;
}

function scrollToPendingPosition(pending: PendingWorkspaceScroll) {
  if (pendingWorkspaceScroll?.token !== pending.token) return;
  window.scrollTo({ behavior: "auto", left: 0, top: pending.targetTop });
}

function maintainPendingPosition(pending: PendingWorkspaceScroll) {
  if (pendingWorkspaceScroll?.token !== pending.token) return;
  scrollToPendingPosition(pending);
  pendingFrame = window.requestAnimationFrame(() => {
    maintainPendingPosition(pending);
  });
}

function holdWorkspaceDocumentScroll(targetHref: string, targetTop: number) {
  const root = document.documentElement;
  const requiredHeight = Math.ceil(targetTop + window.innerHeight);
  const heldHeight = Math.max(
    requiredHeight,
    root.scrollHeight,
    document.body.scrollHeight,
  );

  pendingWorkspaceScroll = {
    href: targetHref,
    minHeight: root.style.minHeight,
    overflowAnchor: root.style.overflowAnchor,
    targetTop,
    token: ++pendingToken,
  };
  root.style.minHeight = `${heldHeight}px`;
  root.style.overflowAnchor = "none";
}

export function prepareWorkspaceDocumentScroll(
  currentHref: string,
  targetHref: string,
) {
  if (typeof window === "undefined") return;

  releasePendingWorkspaceScroll();

  const currentTop = Math.max(0, window.scrollY);
  workspaceScrollPositions.set(currentHref, currentTop);

  const targetTop =
    currentHref === targetHref
      ? 0
      : (workspaceScrollPositions.get(targetHref) ?? 0);
  holdWorkspaceDocumentScroll(targetHref, targetTop);
}

export function resetWorkspaceDocumentScroll(targetHref: string) {
  if (typeof window === "undefined") return;

  releasePendingWorkspaceScroll();
  holdWorkspaceDocumentScroll(targetHref, 0);
  restoreWorkspaceDocumentScroll(targetHref);
}

export function restoreWorkspaceDocumentScroll(targetHref: string) {
  // Every entry without a prepared dock destination starts at the top. A
  // module-wide first-visit flag lets admin (or other pages) leak their scroll
  // position into the workspace when its shell mounts again.
  if (!pendingWorkspaceScroll) {
    holdWorkspaceDocumentScroll(targetHref, 0);
  }

  const pending = pendingWorkspaceScroll;
  if (!pending || pending.href !== targetHref) return;
  // Version switches start restoration before routing; the destination shell
  // may request the same restoration again when it mounts.
  if (pendingCleanupTimer !== null) return;

  maintainPendingPosition(pending);

  const userInputEvents = ["keydown", "pointerdown", "touchstart", "wheel"];
  const removeUserInputListeners = () => {
    for (const eventName of userInputEvents) {
      window.removeEventListener(eventName, cancelForUserInput);
    }
  };
  const cancelForUserInput = () => {
    removeUserInputListeners();
    releasePendingWorkspaceScroll(pending.token);
  };
  for (const eventName of userInputEvents) {
    window.addEventListener(eventName, cancelForUserInput, {
      once: true,
      passive: true,
    });
  }

  pendingCleanupTimer = window.setTimeout(() => {
    removeUserInputListeners();

    if (pendingWorkspaceScroll?.token !== pending.token) return;

    scrollToPendingPosition(pending);
    releasePendingWorkspaceScroll(pending.token);
    window.requestAnimationFrame(() => {
      window.scrollTo({
        behavior: "auto",
        left: 0,
        top: pending.targetTop,
      });
    });
  }, 800);
}

export function cancelWorkspaceDocumentScrollRestore() {
  if (typeof window === "undefined") return;
  releasePendingWorkspaceScroll();
}

export function resetWorkspaceDocumentScrollForTest() {
  if (typeof window !== "undefined") releasePendingWorkspaceScroll();
  workspaceScrollPositions.clear();
}
